import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../components/layout/Header';
import { getLineProtection, updateLineProtection } from '../services/apiCampaigns';
import { EstadoDeLinea } from '../components/campaigns/shared';

const DIAS = [
  ['mon', 'Lun'], ['tue', 'Mar'], ['wed', 'Mié'], ['thu', 'Jue'], ['fri', 'Vie'], ['sat', 'Sáb'], ['sun', 'Dom'],
];

const campoClase = 'w-24 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-sm font-bold text-slate-900 dark:text-white text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40';

const Grupo = ({ icono, titulo, porQue, children }) => (
  <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6">
    <div className="flex items-start gap-3 mb-5">
      <span className="material-symbols-outlined text-primary">{icono}</span>
      <div>
        <h3 className="font-bold text-slate-900 dark:text-white">{titulo}</h3>
        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5 max-w-2xl">{porQue}</p>
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Numero = ({ etiqueta, unidad, valor, onChange, rango, recomendado, ayuda }) => (
  <div className="flex items-center justify-between gap-4 flex-wrap">
    <div className="min-w-0">
      <p className="text-sm font-bold text-slate-800 dark:text-gray-100">{etiqueta}</p>
      {ayuda && <p className="text-[11px] text-slate-500 dark:text-gray-400 max-w-lg">{ayuda}</p>}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <input type="number" className={campoClase} value={valor ?? ''} min={rango?.min} max={rango?.max}
        onChange={e => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
      <span className="text-xs text-slate-500 w-20">{unidad}</span>
      {recomendado !== undefined && valor !== recomendado && (
        <button type="button" onClick={() => onChange(recomendado)} className="text-[11px] font-bold text-primary hover:underline">
          Recomendado: {recomendado}
        </button>
      )}
    </div>
  </div>
);

export default function CampaignProtection() {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState(null);
  const [errores, setErrores] = useState({});
  const [estado, setEstado] = useState(null);

  const { data } = useQuery({
    queryKey: ['campaigns-protection'],
    queryFn: async () => (await getLineProtection()).data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (data && !config) setConfig(data.config);
  }, [data, config]);

  if (!data || !config) return <div className="p-8 text-text-secondary">Cargando…</div>;

  const def = data.por_defecto;
  const r = data.rangos;
  const set = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));
  const num = (k) => ({ valor: config[k], onChange: v => set(k, v), rango: r[k], recomendado: def[k] });

  const guardar = async () => {
    setErrores({});
    setEstado('guardando');
    try {
      const { zona_horaria, ...datos } = config; // eslint-disable-line no-unused-vars
      const { data: nuevo } = await updateLineProtection(datos);
      setConfig(nuevo.config);
      queryClient.setQueryData(['campaigns-protection'], nuevo);
      setEstado('ok');
      setTimeout(() => setEstado(null), 2500);
    } catch (err) {
      setErrores(err.response?.data?.errors ?? {});
      setEstado(null);
    }
  };

  const alternarDia = (d) => set('dias', config.dias.includes(d) ? config.dias.filter(x => x !== d) : [...config.dias, d]);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
      <Header title="Protección de la línea" />

      <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
        <div className="max-w-4xl mx-auto space-y-6">
          <Link to="/campaigns" className="text-sm font-bold text-slate-500 hover:text-primary inline-flex items-center gap-1">
            <span className="material-symbols-outlined text-[18px]">arrow_back</span> Campañas
          </Link>

          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Protección de la línea</h2>
            <p className="text-sm text-text-secondary max-w-3xl">
              WhatsApp bloquea los números que mandan mucho, rápido, el mismo texto o a gente que no los conoce. Estos límites valen para todas las campañas a la vez y se suman a los frenos que la línea ya tiene. Los valores recomendados son conservadores a propósito: si la línea es la de ventas, perderla cuesta más que cualquier campaña.
            </p>
          </div>

          {data.lineas?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.lineas.map(l => <EstadoDeLinea key={l.linea_id} linea={l} />)}
            </div>
          )}

          <Grupo icono="speed" titulo="Ritmo" porQue="Cuántos mensajes de campaña salen y cada cuánto. Lo que más delata a un bot es el volumen parejo y rápido.">
            <Numero etiqueta="Máximo por hora" unidad="por línea" {...num('max_por_hora')} />
            <Numero etiqueta="Máximo en 24 horas" unidad="por línea" {...num('max_por_dia')} />
            <Numero etiqueta="Espera mínima entre mensajes" unidad="segundos" {...num('pausa_min_seg')} />
            <Numero etiqueta="Espera máxima entre mensajes" unidad="segundos" {...num('pausa_max_seg')}
              ayuda="Cada mensaje espera un tiempo al azar dentro de este rango: nunca intervalos exactos." />
          </Grupo>

          <Grupo icono="trending_up" titulo="Calentamiento" porQue="Una línea que nunca mandó campañas no puede pasar de cero a cien en un día. El tope diario arranca bajo y sube con cada día de envíos.">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={!!config.calentamiento} onChange={e => set('calentamiento', e.target.checked)} />
              <span className="text-sm font-bold text-slate-800 dark:text-gray-100">Calentar la línea</span>
            </label>
            {config.calentamiento && (
              <>
                <Numero etiqueta="Primer día" unidad="mensajes" {...num('calentamiento_inicio')} />
                <Numero etiqueta="Aumento por día con envíos" unidad="mensajes" {...num('calentamiento_incremento')} />
              </>
            )}
          </Grupo>

          <Grupo icono="support_agent" titulo="Reserva para la atención en vivo" porQue="La línea tiene un techo por hora según su antigüedad. Una parte queda siempre para atender a los clientes: una campaña nunca puede dejar sin capacidad a quien escribe.">
            <Numero etiqueta="Reservado para atender" unidad="% del techo" {...num('reserva_atencion_pct')} />
          </Grupo>

          <Grupo icono="schedule" titulo="Horario de envío" porQue="En la hora del negocio. Fuera de esto no sale nada: un mensaje comercial a las 11 de la noche es una denuncia segura.">
            <div className="flex flex-wrap gap-1.5">
              {DIAS.map(([d, etiqueta]) => (
                <button key={d} type="button" onClick={() => alternarDia(d)} aria-pressed={config.dias.includes(d)}
                  className={`text-xs font-bold w-12 py-1.5 rounded-lg border ${config.dias.includes(d) ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-white/10 text-slate-500'}`}>
                  {etiqueta}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input type="time" className={campoClase + ' w-32'} value={config.hora_inicio} onChange={e => set('hora_inicio', e.target.value)} />
              <span className="text-sm text-slate-500">a</span>
              <input type="time" className={campoClase + ' w-32'} value={config.hora_fin} onChange={e => set('hora_fin', e.target.value)} />
              <span className="text-xs text-slate-400">({config.zona_horaria})</span>
            </div>
            {errores.hora_fin && <p className="text-xs text-red-600">La hora de fin tiene que ser posterior a la de inicio.</p>}
          </Grupo>

          <Grupo icono="person_off" titulo="A quién nunca" porQue="Además de quien pidió la baja, quien nunca escribió y quien tiene una escalación abierta, que no se pueden cambiar.">
            <Numero etiqueta="No repetir campaña al mismo contacto antes de" unidad="días" {...num('no_repetir_dias')} />
            <Numero etiqueta="Sólo si escribió en los últimos" unidad="días" {...num('ultimo_mensaje_max_dias')}
              ayuda="Quien no habla con el negocio hace mucho ya no lo reconoce, y es quien más denuncia como spam." />
            <Numero etiqueta="No interrumpir conversaciones de las últimas" unidad="horas" {...num('actividad_min_horas')} />
          </Grupo>

          <Grupo icono="front_hand" titulo="Pausa automática" porQue="Ante señales de problema, todas las campañas de la línea se pausan solas y se avisa a los managers. Reanudar es un clic; recuperar una línea bloqueada, no.">
            <Numero etiqueta="Si fallan seguidos" unidad="envíos" {...num('pausa_si_fallos_seguidos')} />
            <Numero etiqueta="Si llega menos del" unidad="% entregado" {...num('pausa_si_entrega_bajo_pct')}
              ayuda="Una línea restringida sigue aceptando envíos que nunca llegan: es la señal más temprana de un bloqueo." />
          </Grupo>

          <Grupo icono="logout" titulo="Texto de baja" porQue="Se agrega al final de cada mensaje (se puede desactivar por campaña). Quien responde BAJA deja de recibir campañas para siempre y recibe una confirmación.">
            <input className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm text-slate-900 dark:text-white"
              value={config.pie_baja ?? ''} maxLength={200} onChange={e => set('pie_baja', e.target.value)} />
          </Grupo>

          <div className="flex items-center justify-end gap-3 sticky bottom-0 -mx-4 px-4 py-3 bg-slate-50/95 dark:bg-background-dark/95 backdrop-blur border-t border-slate-200 dark:border-white/10">
            {Object.keys(errores).length > 0 && <p className="text-xs text-red-600">Revisa los valores marcados: hay alguno fuera de rango.</p>}
            {estado === 'ok' && <p className="text-xs font-bold text-emerald-600">Guardado</p>}
            <button onClick={() => setConfig({ ...def, zona_horaria: config.zona_horaria })}
              className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/5">
              Volver a los recomendados
            </button>
            <button onClick={guardar} disabled={estado === 'guardando'}
              className="bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-xl font-bold text-sm shadow-sm disabled:opacity-50">
              {estado === 'guardando' ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
